import { CONDITION_OPERATOR, TConditionExpression, TConditionGroup, TFieldCondition, TFieldValue } from '../models';

// Resolves the current value of a source key (a custom field id, question id or
// platform field key) so conditions can be evaluated against live answers.
export type TValueResolver = (sourceKey: string) => TFieldValue | undefined;

const isGroup = (expr: TConditionExpression): expr is TConditionGroup =>
  (expr as TConditionGroup).combinator !== undefined;

const isAnswered = (value: TFieldValue | undefined): boolean => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

// Normalizes any field value to a comparable set of primitive strings so that
// equals/contains/in behave consistently across single values and arrays.
const toComparableList = (value: TFieldValue | undefined): string[] => {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) {
    return value.map((entry) => (typeof entry === 'object' ? entry.id : String(entry)));
  }
  if (typeof value === 'object') return [value.id];
  return [String(value)];
};

const asString = (value: TFieldValue | undefined): string => {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value))
    return value.map((entry) => (typeof entry === 'object' ? entry.id : String(entry))).join(',');
  if (typeof value === 'object') return value.id;
  return String(value);
};

const evaluateLeaf = (condition: TFieldCondition, resolve: TValueResolver): boolean => {
  const current = resolve(condition.sourceKey);
  const target = condition.value;

  switch (condition.operator) {
    case CONDITION_OPERATOR.IS_ANSWERED:
      return isAnswered(current);
    case CONDITION_OPERATOR.EQUALS:
      return asString(current) === asString(target);
    case CONDITION_OPERATOR.NOT_EQUALS:
      return asString(current) !== asString(target);
    case CONDITION_OPERATOR.CONTAINS: {
      // The current value (single or multi) contains the target token.
      const targetToken = asString(target);
      return toComparableList(current).includes(targetToken);
    }
    case CONDITION_OPERATOR.IN: {
      // The current value is one of the target list.
      const currentToken = asString(current);
      return toComparableList(target).includes(currentToken);
    }
    default:
      return false;
  }
};

// Evaluates a condition expression (single condition or nested AND/OR group).
// An absent expression means "always true" (unconditional).
export const evaluateCondition = (expression: TConditionExpression | undefined, resolve: TValueResolver): boolean => {
  if (!expression) return true;
  if (!isGroup(expression)) return evaluateLeaf(expression, resolve);
  if (expression.conditions.length === 0) return true;
  return expression.combinator === 'and'
    ? expression.conditions.every((child) => evaluateCondition(child, resolve))
    : expression.conditions.some((child) => evaluateCondition(child, resolve));
};

// A short, human-readable explanation of a single-condition expression, used for
// the subtle "Shown because ..." note. Returns undefined for complex groups.
export const describeCondition = (
  expression: TConditionExpression | undefined,
  labelFor: (sourceKey: string) => string
): string | undefined => {
  if (!expression || isGroup(expression)) return undefined;
  const label = labelFor(expression.sourceKey);
  const value = asString(expression.value);
  switch (expression.operator) {
    case CONDITION_OPERATOR.IS_ANSWERED:
      return `Shown because ${label} is answered`;
    case CONDITION_OPERATOR.EQUALS:
      return `Shown because ${label} is ${value}`;
    case CONDITION_OPERATOR.NOT_EQUALS:
      return `Shown because ${label} is not ${value}`;
    case CONDITION_OPERATOR.CONTAINS:
      return `Shown because ${label} includes ${value}`;
    case CONDITION_OPERATOR.IN:
      return `Shown because ${label} is one of ${value}`;
    default:
      return undefined;
  }
};
